import React from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { BackButton } from '../../components/ui';

const ContentCommunityGuidelines = () => {
  const navigate = useNavigate();

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-gray-900 text-white py-4 px-6">
        <div className="flex items-center justify-between">
          <div className="flex items-center">
            <div className="w-3 h-3 bg-orange-500 rounded-full mr-3" aria-hidden="true"></div>
            <span className="text-lg font-semibold">Vesta • Legal Center</span>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <BackButton
              onClick={() => navigate('/legal')}
              variant="link"
              ariaLabel="Back to Legal Center"
            />
            <span className="text-blue-600 hover:text-blue-500 font-medium">
              Back to Legal Center
            </span>
          </div>
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        {/* Policy Header */}
        <div className="bg-white rounded-2xl shadow-lg p-8 mb-8">
          <h1 className="text-3xl font-bold text-gray-900 mb-4">
            Content & Community Guidelines
          </h1>
          <p className="text-gray-600 mb-4">
            Effective Date: [Insert Date]
          </p>
          <p className="text-gray-700">
            Vesta is a community built on trust, creativity, and safe food practices. These guidelines apply to all users—Sellers, Buyers, and Guests—and cover all content you create, share, or post on the Platform, including videos, photos, listing descriptions, shop pages, reviews, and messages.
          </p>
          <div className="mt-4 p-4 bg-red-50 border border-red-200 rounded-lg">
            <p className="text-red-800 font-semibold">
              Violating these guidelines may result in content removal, account suspension, or permanent termination.
            </p>
          </div>
        </div>

        {/* Policy Content */}
        <div className="space-y-8">
          {/* Section 1 */}
          <section className="bg-white rounded-xl shadow-md p-6">
            <h2 className="text-2xl font-bold text-gray-900 mb-4">
              1. Truthful & Accurate Representation
            </h2>
            <ul className="list-disc list-inside space-y-2 text-gray-700 ml-4">
              <li>Show the actual product or service being offered.</li>
              <li>Use original photos and videos of your own work—no stock or stolen media.</li>
              <li>Avoid misleading filters, edits, or angles that materially change the appearance of the product.</li>
              <li>Disclose when media is AI-generated or heavily edited.</li>
              <li>For services, represent the scope, skill, and conditions honestly.</li>
            </ul>
          </section>

          {/* Section 2 */}
          <section className="bg-white rounded-xl shadow-md p-6">
            <h2 className="text-2xl font-bold text-gray-900 mb-4">
              2. Safe Food Handling Depictions
            </h2>
            <ul className="list-disc list-inside space-y-2 text-gray-700 ml-4">
              <li>Demonstrate sanitary preparation and storage practices in all videos/photos.</li>
              <li>No depiction of unsafe handling (e.g., raw meat cross-contact, unrefrigerated perishables).</li>
              <li>Avoid staged hazards for entertainment purposes.</li>
              <li>Do not encourage or glorify dangerous eating challenges.</li>
            </ul>
          </section>

          {/* Section 3 */}
          <section className="bg-white rounded-xl shadow-md p-6">
            <h2 className="text-2xl font-bold text-gray-900 mb-4">
              3. Respectful & Inclusive Interaction
            </h2>
            <ul className="list-disc list-inside space-y-2 text-gray-700 ml-4">
              <li>No harassment, hate speech, or discriminatory remarks based on race, ethnicity, religion, gender, sexual orientation, disability, or other protected status.</li>
              <li>Communicate respectfully with all users, both publicly and privately.</li>
              <li>Do not post content intended to shame or attack other sellers, buyers, or Vesta staff.</li>
            </ul>
          </section>

          {/* Section 4 */}
          <section className="bg-white rounded-xl shadow-md p-6">
            <h2 className="text-2xl font-bold text-gray-900 mb-4">
              4. Intellectual Property
            </h2>
            <ul className="list-disc list-inside space-y-2 text-gray-700 ml-4">
              <li>Only post content you own or have rights to use.</li>
              <li>No unauthorized use of copyrighted music, images, logos, or trademarks.</li>
              <li>Report suspected IP violations through our <Link to="/legal/intellectual-property-dmca" className="text-blue-600 hover:text-blue-500 underline">Intellectual Property & DMCA Policy</Link>.</li>
            </ul>
          </section>

          {/* Section 5 */}
          <section className="bg-white rounded-xl shadow-md p-6">
            <h2 className="text-2xl font-bold text-gray-900 mb-4">
              5. Prohibited Content Types
            </h2>
            <ul className="list-disc list-inside space-y-2 text-gray-700 ml-4">
              <li>Graphic depictions of injury, illness, or unsafe kitchen environments.</li>
              <li>Sexual or sexually suggestive content in a food/service context.</li>
              <li>Content promoting illegal activity, including sale of prohibited foods or services.</li>
              <li>False health/medical claims about food or beverages.</li>
            </ul>
          </section>

          {/* Section 6 */}
          <section className="bg-white rounded-xl shadow-md p-6">
            <h2 className="text-2xl font-bold text-gray-900 mb-4">
              6. Video & Media Quality Standards
            </h2>
            <ul className="list-disc list-inside space-y-2 text-gray-700 ml-4">
              <li>Videos should clearly show the product or service, avoiding excessive motion blur or darkness.</li>
              <li>Audio, if included, must be relevant and appropriate.</li>
              <li>Backgrounds should be clean and free of unrelated hazards.</li>
              <li>Allergen and ingredient info must be shown in the listing or description, even if mentioned in video.</li>
            </ul>
          </section>

          {/* Section 7 */}
          <section className="bg-white rounded-xl shadow-md p-6">
            <h2 className="text-2xl font-bold text-gray-900 mb-4">
              7. Review & Feedback Conduct
            </h2>
            <ul className="list-disc list-inside space-y-2 text-gray-700 ml-4">
              <li>Reviews must reflect genuine experiences.</li>
              <li>No incentivizing, buying, or posting fake reviews.</li>
              <li>Avoid retaliatory reviews or public harassment of reviewers.</li>
            </ul>
          </section>

          {/* Section 8 */}
          <section className="bg-white rounded-xl shadow-md p-6">
            <h2 className="text-2xl font-bold text-gray-900 mb-4">
              8. Consequences for Violations
            </h2>
            <ul className="list-disc list-inside space-y-2 text-gray-700 ml-4">
              <li>Content removal.</li>
              <li>Temporary or permanent account suspension.</li>
              <li>Removal from search results or category pages.</li>
              <li>Loss of selling privileges.</li>
            </ul>
          </section>

          {/* Section 9 */}
          <section className="bg-white rounded-xl shadow-md p-6">
            <h2 className="text-2xl font-bold text-gray-900 mb-4">
              9. Policy Updates
            </h2>
            <p className="text-gray-700">
              We may update these guidelines to reflect changes in industry standards, community feedback, or legal requirements. Continued use of Vesta after updates constitutes acceptance.
            </p>
          </section>

          {/* Section 10 */}
          <section className="bg-white rounded-xl shadow-md p-6">
            <h2 className="text-2xl font-bold text-gray-900 mb-4">
              10. Content Moderation & Appeals
            </h2>
            <ul className="list-disc list-inside space-y-2 text-gray-700 ml-4">
              <li>Content is reviewed based on reported violations and automated detection.</li>
              <li>Users may appeal content removal decisions within 7 days.</li>
              <li>Appeals are reviewed by senior moderation staff.</li>
              <li>Final decisions are binding and not subject to further review.</li>
            </ul>
          </section>

          {/* Section 11 */}
          <section className="bg-white rounded-xl shadow-md p-6">
            <h2 className="text-2xl font-bold text-gray-900 mb-4">
              11. Community Reporting
            </h2>
            <ul className="list-disc list-inside space-y-2 text-gray-700 ml-4">
              <li>Users are encouraged to report violations of these guidelines.</li>
              <li>Reports should include specific details and evidence.</li>
              <li>False or malicious reports may result in account penalties.</li>
              <li>Anonymous reporting is available for sensitive situations.</li>
            </ul>
          </section>

          {/* Section 12 */}
          <section className="bg-white rounded-xl shadow-md p-6">
            <h2 className="text-2xl font-bold text-gray-900 mb-4">
              12. Professional Standards
            </h2>
            <ul className="list-disc list-inside space-y-2 text-gray-700 ml-4">
              <li>Sellers must maintain professional conduct in all interactions.</li>
              <li>Business communications should be courteous and helpful.</li>
              <li>No aggressive marketing or unsolicited contact.</li>
              <li>Respect customer privacy and boundaries.</li>
            </ul>
          </section>

          {/* Section 13 */}
          <section className="bg-white rounded-xl shadow-md p-6">
            <h2 className="text-2xl font-bold text-gray-900 mb-4">
              13. Food Safety & Compliance
            </h2>
            <ul className="list-disc list-inside space-y-2 text-gray-700 ml-4">
              <li>All content must demonstrate safe food handling practices.</li>
              <li>No content that could mislead consumers about food safety.</li>
              <li>Allergen information must be accurate and prominently displayed.</li>
              <li>Follow local food safety regulations in all content.</li>
            </ul>
          </section>

          {/* Section 14 */}
          <section className="bg-white rounded-xl shadow-md p-6">
            <h2 className="text-2xl font-bold text-gray-900 mb-4">
              14. Accessibility & Inclusivity
            </h2>
            <ul className="list-disc list-inside space-y-2 text-gray-700 ml-4">
              <li>Content should be accessible to users with disabilities.</li>
              <li>Provide alternative text for images when possible.</li>
              <li>Use clear, readable fonts and high contrast.</li>
              <li>Consider diverse dietary needs and preferences.</li>
            </ul>
          </section>

          {/* Section 15 */}
          <section className="bg-white rounded-xl shadow-md p-6">
            <h2 className="text-2xl font-bold text-gray-900 mb-4">
              15. Contact Information
            </h2>
            <p className="text-gray-700 mb-4">
              For questions about these guidelines:
            </p>
            <ul className="list-disc list-inside space-y-2 text-gray-700 ml-4">
              <li>Email: guidelines@vesta.com</li>
              <li>Community Support: community@vesta.com</li>
              <li>Phone: [Insert Phone Number]</li>
            </ul>
          </section>

          {/* Section 16 */}
          <section className="bg-white rounded-xl shadow-md p-6">
            <h2 className="text-2xl font-bold text-gray-900 mb-4">
              16. Enforcement Process
            </h2>
            <ul className="list-disc list-inside space-y-2 text-gray-700 ml-4">
              <li>First violation: Warning and content removal.</li>
              <li>Second violation: Temporary account suspension (7-30 days).</li>
              <li>Third violation: Extended suspension (30-90 days).</li>
              <li>Fourth violation: Permanent account termination.</li>
            </ul>
          </section>

          {/* Final Statement */}
          <section className="bg-orange-50 border border-orange-200 rounded-xl p-6">
            <p className="text-orange-800 text-center font-medium">
              By using Vesta, you acknowledge that you have read, understood, and agree to be bound by these Content & Community Guidelines.
            </p>
          </section>
        </div>

        {/* Footer Navigation */}
        <div className="mt-12 flex justify-center">
          <Link
            to="/legal"
            className="bg-orange-600 text-white px-6 py-3 rounded-lg font-semibold hover:bg-orange-700 transition-colors"
            aria-label="Return to Legal Center"
          >
            Return to Legal Center
          </Link>
        </div>
      </main>
    </div>
  );
};

export default ContentCommunityGuidelines; 